import { PROJECT_TYPE_LABELS, type QuestionDefinition } from "@reno-estimator/core";
import { commonQuestions, getQuestionSetForProjectType, isQuestionVisible } from "@reno-estimator/config";
import { QuestionRenderer } from "../../ui/QuestionRenderer.js";
import type { EstimatorFlow } from "../../../state/useEstimatorFlow.js";

/**
 * Reconstructed per docs/estimator-ui.md and docs/dev-instructions.md's
 * description of the Phase 4 reusable wizard framework: `QuestionRenderer`
 * renders one QuestionDefinition, and this component is the piece that
 * loops over a project type's own questions (excluding the common ones,
 * which Step3WhatChanging already renders by hand above this component)
 * and decides which of them are currently visible, given the answer bag.
 *
 * This is deliberately generic - it has no per-project-type branching. A
 * new project type's questions (packages/config/src/questions/<type>.ts,
 * registered in questions/registry.ts) show up here automatically with no
 * change to this file, which is the entire point of the config/UI split
 * documented in docs/architecture.md.
 */

const COMMON_QUESTION_IDS = new Set(commonQuestions.map((q) => q.id));

/** The current project type's own questions (i.e. the full set minus the common ones rendered elsewhere), in declared order. */
function getProjectSpecificQuestionDefinitions(flow: EstimatorFlow): QuestionDefinition[] {
  if (!flow.projectType) return [];
  return getQuestionSetForProjectType(flow.projectType).questions.filter((q) => !COMMON_QUESTION_IDS.has(q.id));
}

/** Of those, the ones currently visible given the answer bag (a question hidden by displayIf is never "required" to answer). */
function getVisibleProjectSpecificQuestions(flow: EstimatorFlow): QuestionDefinition[] {
  return getProjectSpecificQuestionDefinitions(flow).filter((q) => isQuestionVisible(q, flow.answers));
}

/**
 * Visible, required, project-specific questions with no stored answer yet -
 * used by Step3WhatChanging to disable "Next" and to name what's still
 * needed, the same way the common tri-state/scope questions already do.
 */
export function getUnansweredRequiredProjectQuestions(flow: EstimatorFlow): QuestionDefinition[] {
  return getVisibleProjectSpecificQuestions(flow).filter((q) => q.required && flow.getAnswer(q.id) === undefined);
}

export function ProjectSpecificQuestions({ flow }: { flow: EstimatorFlow }) {
  const questions = getVisibleProjectSpecificQuestions(flow);
  if (!flow.projectType || questions.length === 0) return null;

  const projectLabel = PROJECT_TYPE_LABELS[flow.projectType] ?? String(flow.projectType);

  return (
    <div className="project-specific-questions">
      <h2 className="project-specific-questions__heading">{projectLabel} details</h2>
      {questions.map((question) => (
        <QuestionRenderer
          key={question.id}
          question={question}
          value={flow.getAnswer(question.id)}
          onChange={(value) => flow.setAnswer(question.id, value)}
        />
      ))}
    </div>
  );
}
